import { useState, useRef, useCallback, useMemo } from 'react';

interface UseShiftSelectOptions<T extends { id: string }> {
  items: T[];
}

interface UseShiftSelectReturn<T extends { id: string }> {
  selectedItems: string[];
  selectedItemsData: Map<string, T>;
  setSelectedItems: React.Dispatch<React.SetStateAction<string[]>>;
  handleSelect: (id: string, index: number, shiftKey: boolean, item: T) => void;
  handleSelectAll: (checked: boolean) => void;
  clearSelection: () => void;
  getSelectedTotal: (key: keyof T) => number;
}

/**
 * Hook for managing multi-item selection with Shift+Click support
 * 
 * Enables selecting a range of items by:
 * 1. Clicking an item to select it (stores as lastSelectedIndex)
 * 2. Holding Shift and clicking another item
 * 3. All items between the two clicks are selected
 * 
 * Now also stores full item data for accurate calculations across pages.
 */
export function useShiftSelect<T extends { id: string }>({
  items,
}: UseShiftSelectOptions<T>): UseShiftSelectReturn<T> {
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [selectedItemsData, setSelectedItemsData] = useState<Map<string, T>>(new Map());
  const lastSelectedIndex = useRef<number | null>(null);

  const handleSelect = useCallback(
    (id: string, index: number, shiftKey: boolean, item: T) => {
      if (shiftKey && lastSelectedIndex.current !== null) {
        // Shift+Click: select range
        const start = Math.min(lastSelectedIndex.current, index);
        const end = Math.max(lastSelectedIndex.current, index);
        
        const rangeItems = items.slice(start, end + 1);
        const rangeIds = rangeItems.map(item => item.id);
        
        setSelectedItems(prev => {
          const newSelection = new Set([...prev, ...rangeIds]);
          return Array.from(newSelection);
        });
        
        setSelectedItemsData(prev => {
          const newData = new Map(prev);
          rangeItems.forEach(rangeItem => {
            newData.set(rangeItem.id, rangeItem);
          });
          return newData;
        });
      } else {
        // Normal click: toggle individual item
        setSelectedItems(prev => {
          if (prev.includes(id)) {
            return prev.filter(itemId => itemId !== id);
          } else {
            return [...prev, id];
          }
        });
        
        setSelectedItemsData(prev => {
          const newData = new Map(prev);
          if (newData.has(id)) {
            newData.delete(id);
          } else {
            newData.set(id, item);
          }
          return newData;
        });
        
        lastSelectedIndex.current = index;
      }
    },
    [items]
  );

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (checked) {
        setSelectedItems(items.map(item => item.id));
        setSelectedItemsData(new Map(items.map(item => [item.id, item])));
      } else {
        setSelectedItems([]);
        setSelectedItemsData(new Map());
        lastSelectedIndex.current = null;
      }
    },
    [items]
  );

  const clearSelection = useCallback(() => {
    setSelectedItems([]);
    setSelectedItemsData(new Map());
    lastSelectedIndex.current = null;
  }, []);

  const getSelectedTotal = useCallback(
    (key: keyof T): number => {
      let total = 0;
      selectedItemsData.forEach(item => {
        const value = item[key];
        if (typeof value === 'number') {
          total += value;
        }
      });
      return total;
    },
    [selectedItemsData]
  );

  return {
    selectedItems,
    selectedItemsData,
    setSelectedItems,
    handleSelect,
    handleSelectAll,
    clearSelection,
    getSelectedTotal,
  };
}
